import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import useFleetAircraftCount from '../hooks/useFleetAircraftCount';
import useAirlineOps from '../hooks/useAirlineOps';
import { fetchScreenshots } from '../api/opsApi';

// The map carries the world's coastlines and borders, which is the biggest thing on the page.
// Loaded after the rest so it never holds up the first paint.
const LiveMap = lazy(() => import('../components/LiveMap'));

const partners = [
  ['https://vacchun.hu/', '/images/partners/vacc-hun.png', 'vACCHUN', 'hun'],
  ['https://ezy887.ch/', '/images/partners/simref.png', 'simref', 'simref'],
  ['https://vatsim.net', '/images/partners/vatsim1.png', 'VATSIM', 'vatsim'],
  ['https://inibuilds.com', '/images/partners/inibuilds.png', 'iniBuilds', 'inibuilds'],
  ['https://vacc-slovakia.sk/', '/images/partners/slovakia.png', 'vacc-slovakia', 'slovakia'],
  ['https://xu.ivao.aero/', '/images/partners/ivao-partner-v.png', 'IVAO Partner VA', 'ivao'],
];

const systems = [
  {
    name: 'Aviofp',
    to: '/aviofp-overview',
    image: '/images/aviofp/overview.png',
    alt: 'The Aviofp Overview page for a selected sector',
    text: 'The flight operations application: the operational flight plan, the briefing, the navigation log, the journey log and the airport briefings for each sector.',
  },
  {
    name: 'e-TLB',
    to: '/etlb',
    image: '/images/etlb/preflight-tasks-complete.png',
    alt: 'Completed vWZZ e-TLB aircraft acceptance task board',
    text: 'The electronic technical logbook. Defects, deferred items, servicing and flight records stay with the aircraft registration between sectors.',
  },
  {
    name: 'Interep',
    to: '/interep-overview',
    image: '/images/interep/report-forms.png',
    alt: 'vWZZ Interep operational report form selection',
    text: 'Operational reporting and entry assessment: submission, reviewer correspondence and the final decision kept together.',
  },
];

const whole = (value) => (Number.isFinite(value) ? value.toLocaleString('en-GB') : null);

function AnimatedCounter({ target, suffix = '' }) {
  const [value, setValue] = useState(0);
  const frameRef = useRef();

  useEffect(() => {
    const duration = 1500;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - progress, 3))));
      if (progress < 1) frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target]);

  return <>{value.toLocaleString()}{suffix}</>;
}

function MetarTicker() {
  const [reports, setReports] = useState([]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [status, setStatus] = useState('Loading weather data...');

  useEffect(() => {
    const controller = new AbortController();
    fetch('https://metar.vatsim.net/metar.php?id=LHBP,LROP,EPWA,EGGW,EPGD,LATI', { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`METAR request failed: ${response.status}`);
        return response.text();
      })
      .then((data) => {
        const items = data.trim().split(/\r?\n/).filter(Boolean);
        if (!items.length) throw new Error('Empty METAR response');
        setReports(items);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setStatus('METAR Offline');
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!reports.length) return undefined;
    setStatus(reports[0]);
    const interval = window.setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((current) => (current + 1) % reports.length);
        setVisible(true);
      }, 500);
    }, 4000);
    return () => window.clearInterval(interval);
  }, [reports]);

  useEffect(() => {
    if (reports.length) setStatus(reports[index]);
  }, [index, reports]);

  return <p id="metar-text" style={{ opacity: visible ? 1 : 0 }}>{status}</p>;
}

// The screenshots card. It is on the page whether or not a month has been judged: with a
// winner it shows that, without one it says how to enter.
function ScreenshotsCard() {
  const [entry, setEntry] = useState(undefined);

  useEffect(() => {
    const controller = new AbortController();
    fetchScreenshots({ signal: controller.signal })
      .then((entries) => setEntry(entries[0] ?? null))
      .catch(() => setEntry(null));
    return () => controller.abort();
  }, []);

  return (
    <section className="shot-home">
      {entry ? (
        <Link className="shot-home-image" to="/screenshots" aria-label={`${entry.title}, screenshot of the month`}>
          <img src={entry.image} alt={entry.title} loading="lazy" decoding="async" />
        </Link>
      ) : (
        <div className="shot-home-image is-empty" aria-hidden="true">
          <span>Screenshots</span>
        </div>
      )}
      <div className="shot-home-copy">
        <h2>{entry ? entry.title : 'Shot of the month'}</h2>
        {entry?.credit ? <p className="shots-credit">By {entry.credit}</p> : null}
        <p>
          {entry?.description
            ? entry.description
            : 'Every month our pilots send in their best shot from the line. Post yours in the screenshots channel on Discord during the month.'}
        </p>
        <Link to="/screenshots" className="btn btn-secondary">
          {entry ? 'PREVIOUS WINNERS' : 'SEE THE SCREENSHOTS'}
        </Link>
      </div>
    </section>
  );
}

export default function HomePage() {
  const fleetCount = useFleetAircraftCount();
  const { airborne, flights, bases, stats } = useAirlineOps();

  return (
    <>
      <PageTitle>vWZZ - Wizz Air Virtual Airline | Fly The Greenest</PageTitle>

      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-headline">FLY THE <span className="highlight">GREENEST</span></h1>
          <p className="hero-subtext">
            A Wizz Air virtual airline built around how the real operation runs: real schedules,
            real aircraft, and the same tools from planning through to the journey log.
          </p>
          <div className="cta-buttons">
            <a href="#apply" className="btn btn-primary">JOIN THE PINK FLEET</a>
            <Link to="/aviofp-overview" className="btn btn-secondary">SEE THE TOOLS</Link>
          </div>

          <div className="hero-live">
            <div className="hero-live-item">
              <span className={`live-dot ${airborne ? 'is-on' : ''}`} aria-hidden="true" />
              <strong>{whole(airborne) ?? '--'}</strong>
              <span>in the air now</span>
            </div>
            <div className="hero-live-item">
              <strong>{whole(stats?.flights) ?? '--'}</strong>
              <span>flights &middot; 30 days</span>
            </div>
            <div className="hero-live-item">
              <strong>{whole(stats?.hours) ?? '--'}</strong>
              <span>hours &middot; 30 days</span>
            </div>
          </div>
        </div>
      </section>

      <section className="live-section" aria-label="vWZZ flights in the air">
        <div className="live-shell">
          <Suspense fallback={<div className="live-map-waiting" />}>
            <LiveMap flights={flights} bases={bases} />
          </Suspense>
        </div>
      </section>

      <section className="stats-section">
        <div className="stats-grid">
          <div className="stat-item"><div className="stat-number"><AnimatedCounter target={fleetCount} /></div><div className="stat-label">AIRCRAFT IN FLEET</div></div>
          <div className="stat-item"><div className="stat-number"><AnimatedCounter target={6500} suffix="+" /></div><div className="stat-label">REGISTERED PILOTS</div></div>
          <div className="stat-item"><div className="stat-number">6+</div><div className="stat-label">YEARS OF EXPERIENCE</div></div>
        </div>
        <div className="metar-container"><div className="metar-text-wrapper"><MetarTicker /></div></div>
      </section>

      <section className="systems-section" aria-labelledby="systems-title">
        <h2 id="systems-title" className="section-title">OUR SYSTEMS</h2>
        <p className="section-lead">Three applications built in house, used on every flight.</p>
        <div className="systems-grid">
          {systems.map((system) => (
            <article className="system-card" key={system.name}>
              <Link to={system.to} className="system-card-image" aria-label={`${system.name} overview`}>
                <img src={system.image} alt={system.alt} loading="lazy" decoding="async" />
              </Link>
              <div className="system-card-body">
                <h3>{system.name}</h3>
                <p>{system.text}</p>
                <Link to={system.to}>Read more</Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="features-section">
        <h2 className="section-title">OPERATIONAL EXCELLENCE</h2>
        <div className="features-grid centered-grid">
          <article className="feature-card"><h3 className="feature-title">Real-World FCOM</h3><p className="feature-text">Explore our custom-built FCOM. From detailed SOPs to abnormal procedures, we guide you through every phase of flight with precision.</p></article>
          <article className="feature-card"><h3 className="feature-title">Up-to-date routes</h3><p className="feature-text">Our system continuously pulls real-world data ensuring accurate flight numbers, callsigns, and up-to-date routings.</p></article>
          <article className="feature-card"><h3 className="feature-title">Flight Data Monitoring</h3><p className="feature-text">Our ACARS system analyzes every flight, reviewing fuel compliance, stabilization, and performance to help you fly professionally.</p></article>
        </div>
      </section>

      <ScreenshotsCard />

      <section className="application-section" id="apply">
        <h2 className="section-title">YOUR JOURNEY TO THE LEFT SEAT</h2>
        <div className="steps-container">
          <article className="step-card"><div className="step-number">01</div><h3>Virtual Assessment</h3><p>Start with an assessment covering <strong>Wizz Air history</strong> and basic aviation knowledge.<br /><br /><span className="highlight-text">Requirement: 75% to pass</span></p></article>
          <div className="step-arrow" aria-hidden="true">➜</div>
          <article className="step-card"><div className="step-number">02</div><h3>Study &amp; Explore</h3><p>Unlock access to our operational documents. Use these materials to prepare for an optional <strong>virtual type-rating</strong> exam.</p></article>
          <div className="step-arrow" aria-hidden="true">➜</div>
          <article className="step-card"><div className="step-number">03</div><h3>Virtual Type-Rating</h3><p>A comprehensive exam focused on <strong>procedural details</strong>, aircraft limitations, and SOPs.</p></article>
        </div>
        <div className="apply-cta"><Link to="/apply" className="btn btn-primary">START APPLICATION</Link></div>
      </section>

      <div className="partners-band">
        {partners.map(([href, src, alt, className]) => (
          <a href={href} target="_blank" rel="noopener noreferrer" key={href}><img src={src} className={`logo ${className}`} alt={alt} /></a>
        ))}
      </div>
    </>
  );
}
